import { deleteCookie, getCookie } from 'cookies-next';
import type { FirebaseApp, FirebaseServerApp } from 'firebase/app';
import { getApps, initializeApp, initializeServerApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  setPersistence,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

import { frontEndRoutes } from '@/constants/routes';
import { activeSpaceCookieName, jwtCookieName } from '@/constants/variables';

import { getFirebaseConfig } from './firebase-config';
import {
  getTargetEnvironmentClient,
  getTargetEnvironmentServer,
} from './target-detection';

// Global variable to store the initialized Firebase app
let firebaseClientApp: FirebaseApp | null = null;
let firebaseServerApp: FirebaseServerApp | null = null;

export async function getJWTCookieServer(): Promise<string> {
  const { cookies } = await import('next/headers');
  const cookieStore = await cookies();
  return cookieStore.get(jwtCookieName)?.value || '';
}

export async function initializeFirebaseClientApp(): Promise<FirebaseApp> {
  const target = getTargetEnvironmentClient();
  const appName = `firebaseApp-${target}`;
  const existingApp = getApps().find(app => app.name === appName);

  if (existingApp) {
    firebaseClientApp = existingApp;
  } else {
    firebaseClientApp = initializeApp(getFirebaseConfig(target), appName);
  }

  // Rename auth variable to authInstance to avoid shadowing
  const authInstance = getAuth(firebaseClientApp);
  await setPersistence(authInstance, browserLocalPersistence);

  return firebaseClientApp;
}
// Move initFirebaseApp declaration up
async function initFirebaseApp(): Promise<FirebaseApp | FirebaseServerApp> {
  return initializeFirebaseClientApp();
}

// Move initializeFirebaseServerApp up
async function initializeFirebaseServerApp() {
  const target = await getTargetEnvironmentServer();
  const idToken = await getJWTCookieServer();

  firebaseServerApp = initializeServerApp(
    getFirebaseConfig(target),
    idToken
      ? {
          authIdToken: idToken,
        }
      : {},
  );

  // Rename auth variable to authInstance to avoid shadowing
  const authInstance = getAuth(firebaseServerApp);
  await authInstance.authStateReady();

  return firebaseServerApp;
}

export async function auth() {
  return getAuth(
    typeof window === 'undefined'
      ? await initializeFirebaseServerApp()
      : await initFirebaseApp(),
  );
}

/**
 * Get root domain cookie options based on NEXT_PUBLIC_JWT_DOMAIN_ROOT.
 * Used consistently across all cookie operations that need root-domain scoping
 * (JWT, active space, etc.) so subdomains can share the same cookie.
 *
 * Returns `{ domain: '.how.fm' }` in production, empty object on localhost.
 */
export function getRootDomainCookieOptions(): { domain?: string } {
  const cookieOptions: { domain?: string } = {};
  if (process.env.NEXT_PUBLIC_JWT_DOMAIN_ROOT !== undefined && process.env.NEXT_PUBLIC_JWT_DOMAIN_ROOT !== 'localhost') {
    cookieOptions.domain = process.env.NEXT_PUBLIC_JWT_DOMAIN_ROOT;
  }
  return cookieOptions;
}

/**
 * Get JWT cookie value from client-side
 * @returns JWT token string or empty string if not found
 */
export function getJWTCookieClient(): string {
  const cookie = getCookie(jwtCookieName);
  return typeof cookie === 'string' ? cookie : '';
}

/**
 * Set JWT cookie with proper domain configuration
 * Centralized function - use this for ALL JWT cookie setting operations
 * @param token - JWT token to store
 */
export async function setJWTCookie(token: string): Promise<void> {
  const { setCookie } = await import('cookies-next');
  const cookieOptions = getRootDomainCookieOptions();
  await setCookie(jwtCookieName, token, {
    ...cookieOptions,
    path: '/',
    maxAge: 7 * 24 * 60 * 60,
    sameSite: 'lax',
  });
}

/**
 * Delete JWT cookie with proper domain configuration
 * Centralized function - use this for ALL JWT cookie deletion operations
 * Called during sign-out to clear authentication
 */
export function deleteJWTCookie(): void {
  const cookieOptions = getRootDomainCookieOptions();
  deleteCookie(jwtCookieName, { ...cookieOptions, path: '/' });
}

// ============================================================================
// SPACE COOKIE HELPERS — Root-domain cookie shared with legacy CMS (poc-cms)
// ============================================================================

/**
 * Get active space cookie value from client-side
 * @returns Space ID string or empty string if not found
 */
export function getSpaceCookieClient(): string {
  const cookie = getCookie(activeSpaceCookieName);
  return typeof cookie === 'string' ? cookie : '';
}

/**
 * Get active space cookie value from server-side
 * @returns Space ID string or empty string if not found
 */
export async function getSpaceCookieServer(): Promise<string> {
  const { cookies } = await import('next/headers');
  const cookieStore = await cookies();
  return cookieStore.get(activeSpaceCookieName)?.value ?? '';
}

/**
 * Set active space cookie with root-domain configuration.
 * 30-day expiry so it persists across sessions and is shared with legacy CMS.
 */
export async function setSpaceCookie(spaceId: string): Promise<void> {
  const { setCookie } = await import('cookies-next');
  const cookieOptions = getRootDomainCookieOptions();
  await setCookie(activeSpaceCookieName, spaceId, {
    ...cookieOptions,
    path: '/',
    maxAge: 30 * 24 * 60 * 60,
    sameSite: 'lax',
  });
}

/**
 * Delete active space cookie with root-domain configuration.
 * Called during logout to clear space preference.
 */
export function deleteSpaceCookie(): void {
  const cookieOptions = getRootDomainCookieOptions();
  deleteCookie(activeSpaceCookieName, { ...cookieOptions, path: '/' });
}

// Firebase services initialization

export async function firestore() {
  return getFirestore(
    typeof window === 'undefined'
      ? await initializeFirebaseServerApp()
      : await initFirebaseApp(),
  );
}

export async function getIdToken(): Promise<string | null> {
  const currentAuth = await auth();
  return currentAuth.currentUser?.getIdToken() || null;
}

/**
 * Server-side authentication check
 * Verifies if user has a valid JWT token in cookies AND Firebase can authenticate
 */
export async function checkAuthServer() {
  const { cookies } = await import('next/headers');
  const cookieStore = await cookies();
  const token = cookieStore.get(jwtCookieName);

  const hasToken = token !== undefined && token.value !== '';

  // If no token, not authenticated
  if (hasToken === false) {
    return {
      isAuthenticated: false,
      token: undefined,
      hasValidFirebaseUser: false,
    };
  }

  // Try to validate with Firebase
  try {
    const authInstance = await auth();
    const currentUser = authInstance.currentUser;
    const hasValidFirebaseUser = currentUser !== null && currentUser.uid !== undefined && currentUser.uid !== '';

    return {
      isAuthenticated: hasToken,
      token: token?.value,
      hasValidFirebaseUser,
    };
  } catch {
    // Firebase validation failed - token may be expired
    return {
      isAuthenticated: hasToken,
      token: token?.value,
      hasValidFirebaseUser: false,
    };
  }
}

/**
 * Protect dashboard routes - redirects to sign-in if not authenticated
 * Also checks if Firebase can validate the token (handles expired tokens)
 */
export async function requireAuth() {
  const { isAuthenticated, hasValidFirebaseUser } = await checkAuthServer();

  // No token at all - redirect to sign in
  if (isAuthenticated === false) {
    const { redirect } = await import('next/navigation');
    redirect(frontEndRoutes.auth.signIn);
  }

  // Token exists but Firebase can't validate it (likely expired)
  // Redirect to logout route which clears cookies and then redirects to sign-in
  if (hasValidFirebaseUser === false) {
    const { redirect } = await import('next/navigation');
    redirect('/api/auth/logout');
  }
}

/**
 * Protect auth routes - redirects to dashboard if already authenticated
 * Only redirects if both token exists AND Firebase can validate it
 */
export async function requireNoAuth() {
  const { isAuthenticated, hasValidFirebaseUser } = await checkAuthServer();

  // Only redirect to dashboard if user is FULLY authenticated
  // (token exists AND Firebase can validate it)
  // If token is expired, let user sign in again - the sign-in will set a fresh token
  if (isAuthenticated === true && hasValidFirebaseUser === true) {
    const { redirect } = await import('next/navigation');
    redirect(frontEndRoutes.dashboard.base);
  }
}
